import { Archive, ArrowRight, BookOpen, BrickWall, FlaskConical, History, LayoutTemplate, LifeBuoy, Radar, Server, ShieldCheck, SquareTerminal, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { GUIDES } from '@/modules/marketing/guides';
import { MarketingPage } from '@/modules/marketing/MarketingPage';
import { usePageMeta } from '@/lib/seo';
import { PAGES } from '@/seo/pages';

/**
 * Documentation — the order somebody actually does things in.
 *
 * ## Why this is a path rather than a reference
 *
 * A reference manual is the right shape once you know what you are looking for. Nobody arriving at
 * documentation for the first time does. What they have is a list of servers and no idea which of
 * the things in the sidebar comes first.
 *
 * So this is the sequence: connect servers, make a first safe change, then the two things that keep a
 * fleet healthy afterwards. Each step says what has to be true before it, because "why did this fail"
 * is nearly always "the step before it was not done".
 *
 * ## Every link goes to a real page
 *
 * The detail lives in the feature articles, which are written from the code. Duplicating them here
 * would create a second description of the same behaviour, and the two would disagree within a
 * month. This page is the map; the articles are the territory.
 */

const STEPS = [
  {
    icon: Server,
    step: '01',
    title: 'Connect your servers',
    body: 'Connect any Linux server you can reach over SSH, from any provider, as root or a user with sudo. The platform detects the distribution, package manager, root access, CPU, memory and disk, and pins the host key on the first connection.',
    needs: 'A server, and a password or SSH key for an account on it.',
    to: '/features/server-management',
    link: 'Server management',
  },
  {
    icon: BookOpen,
    step: '02',
    title: 'Run your first playbook',
    body: 'Run a playbook such as Update Packages on a few servers. Dry run it first, keep "Try one server first" on, review the canary server’s output, then continue. Every server is checked before anything changes.',
    needs: 'A connected server running a distribution the playbook supports.',
    to: '/features/playbooks',
    link: 'Linux server playbooks',
  },
  {
    icon: Radar,
    step: '03',
    title: 'Set up monitoring',
    body: 'Add monitors for your sites, ports, certificates and domains, and a server health check for each server. Attach the server so Auto-Fix can try a safe repair before alerting you, and choose where alerts go.',
    needs: 'A public address to check, or a connected server.',
    to: '/features/auto-fix',
    link: 'Auto-Fix',
  },
  {
    icon: Archive,
    step: '04',
    title: 'Set up backups',
    body: 'Add a source (a directory, a database or both), storage you own and a schedule. Each source’s newest backup is restore-tested every week, so a backup that would not restore is found before you need it.',
    needs: 'A connected server, and an S3-compatible bucket or space on the server.',
    to: '/features/backups',
    link: 'Server backups',
  },
];

const MORE = [
  {
    icon: BookOpen,
    title: 'Playbook reference',
    body: 'Every playbook: what it changes on the server, each option explained, and its errors.',
    to: '/docs/playbooks',
  },
  {
    icon: LayoutTemplate,
    title: 'Template reference',
    body: 'Every server template: its steps, its options and what to do after the run.',
    to: '/docs/templates',
  },
  {
    icon: LifeBuoy,
    title: 'Common errors',
    body: 'What each error message means, from connecting a server to backups, and how to fix it.',
    to: '/docs/troubleshooting',
  },
  {
    icon: SquareTerminal,
    title: 'Command runner',
    body: 'Run one command on many servers, review every server’s output and retry the ones that failed.',
    to: '/features/command-runner',
  },
  {
    icon: LayoutTemplate,
    title: 'Server templates',
    body: 'Prepare web, Python, reverse proxy, CI runner, Docker and monitoring servers in one versioned run.',
    to: '/features/server-templates',
  },
  {
    icon: BrickWall,
    title: 'Security hardening',
    body: 'Fail2Ban, a firewall and safer SSH settings, with every change undone if a check fails.',
    to: '/features/security-hardening',
  },
  {
    icon: History,
    title: 'Run history',
    body: 'Who ran what, on which servers, with each server’s output, exit code and duration.',
    to: '/features/run-history',
  },
  {
    icon: ShieldCheck,
    title: 'Safe rollouts',
    body: 'Preflight checks, a canary server and batches that stop when failures pass your limit.',
    to: '/features/safe-rollouts',
  },
  {
    icon: FlaskConical,
    title: 'Backup verification',
    body: 'How weekly restore tests work, what they never touch and what a failure means.',
    to: '/features/backup-verification',
  },
  {
    icon: Radar,
    title: 'Monitoring guides',
    body: 'What each type of check detects and how to configure it.',
    to: '/guides',
  },
  {
    icon: Users,
    title: 'Team access',
    body: 'Invitations, what team members can do, and how removing someone works.',
    to: '/features/team-management',
  },
];

export function DocsPage() {
  usePageMeta(PAGES.docs);

  return (
    <MarketingPage
      eyebrow="Documentation"
      title="Documentation"
      standfirst="Step-by-step guides for starting a trial, connecting servers, running automation, hardening servers, setting up monitoring and managing backups."
      wide
    >
      {/* ## Step-by-step guides come first
          The section below explains the order things happen in, which is what somebody
          *evaluating* the platform wants. Somebody who has already signed up and is stuck on a
          screen wants the steps for that screen, and making them scroll past the theory to reach
          them is how documentation stops being read. */}
      <section className="mt-16">
        <h2 className="text-center text-xl font-semibold tracking-tight sm:text-2xl">Guides</h2>
        <p className="text-muted-foreground mx-auto mt-4 max-w-xl text-center text-sm">
          Each guide covers one task, with controls named exactly as they appear in the dashboard.
        </p>

        <div className="mt-10 grid gap-4 sm:grid-cols-2">
          {GUIDES.map((guide) => {
            const figures = guide.steps.filter((step) => step.image !== undefined).length;

            return (
              <Link
                key={guide.slug}
                to={`/docs/${guide.slug}`}
                className="border-border/60 hover:border-primary/40 hover:bg-muted/30 group flex items-start gap-4 rounded-xl border p-5 transition-colors"
              >
                <span className="bg-primary/10 text-primary flex size-10 shrink-0 items-center justify-center rounded-lg">
                  <guide.icon className="size-5" aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2 font-medium">
                    {guide.title}
                    <ArrowRight className="size-4 opacity-0 transition-opacity group-hover:opacity-70" aria-hidden />
                  </span>
                  <span className="text-muted-foreground mt-1 block text-sm leading-relaxed">{guide.summary}</span>
                  {/* Says what is inside before the click: a guide with pictures of the real
                      screens is a different proposition from one without. */}
                  <span className="text-muted-foreground/70 mt-3 block text-xs">
                    {guide.steps.length} steps
                    {figures > 0 && ` · ${figures} annotated screenshots`}
                  </span>
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="mt-20">
        <h2 className="text-center text-xl font-semibold tracking-tight sm:text-2xl">Getting started</h2>
        <p className="text-muted-foreground mx-auto mt-4 max-w-xl text-center text-sm">
          Follow these steps in order. Each one depends on the step before it.
        </p>

        <div className="mt-12 space-y-6">
          {STEPS.map((step) => (
            <div
              key={step.step}
              className="border-border/60 grid gap-6 rounded-xl border p-6 sm:grid-cols-[auto_1fr] sm:p-8"
            >
              <div className="flex items-start gap-4">
                <span className="bg-primary/10 text-primary inline-flex size-11 shrink-0 items-center justify-center rounded-xl">
                  <step.icon className="size-5" aria-hidden />
                </span>
                <span className="text-muted-foreground/50 text-sm font-medium sm:hidden">{step.step}</span>
              </div>
              <div>
                <div className="flex items-baseline gap-3">
                  <span className="text-muted-foreground/50 hidden text-sm font-medium sm:inline">{step.step}</span>
                  <h3 className="font-medium tracking-tight">{step.title}</h3>
                </div>
                <p className="text-muted-foreground mt-3 text-sm leading-relaxed">{step.body}</p>
                <p className="text-muted-foreground/70 mt-4 text-xs">
                  <span className="text-foreground/70 font-medium">You need:</span> {step.needs}
                </p>
                <Link
                  to={step.to}
                  className="text-primary mt-4 inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
                >
                  {step.link}
                  <ArrowRight className="size-4" aria-hidden />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-24">
        <h2 className="text-center text-xl font-semibold tracking-tight sm:text-2xl">Feature reference</h2>
        <p className="text-muted-foreground mx-auto mt-4 max-w-xl text-center text-sm">
          Detailed explanations of how each part of the platform works.
        </p>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {MORE.map((item) => (
            <Link
              key={item.title}
              to={item.to}
              className="border-border/60 hover:border-primary/40 hover:bg-muted/20 group rounded-xl border p-6 transition-colors"
            >
              <span className="bg-primary/10 text-primary inline-flex size-9 items-center justify-center rounded-lg">
                <item.icon className="size-4.5" aria-hidden />
              </span>
              <h3 className="mt-4 font-medium tracking-tight">{item.title}</h3>
              <p className="text-muted-foreground mt-3 text-sm leading-relaxed">{item.body}</p>
              <span className="text-primary mt-4 inline-flex items-center gap-1.5 text-sm font-medium">
                Learn more
                <span className="sr-only"> about {item.title}</span>
                <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
              </span>
            </Link>
          ))}
        </div>
      </section>

      <div className="mt-20 text-center">
        <Link
          to="/register"
          className="bg-primary text-primary-foreground inline-flex items-center rounded-full px-8 py-3.5 font-medium transition-opacity hover:opacity-90"
        >
          Start free trial
        </Link>
      </div>
    </MarketingPage>
  );
}
